import { BaseEntity } from "./BaseEntity";
import { AutomationMessageSource, AutomationMessageType } from "./Enums";

export interface AutomationMessage extends BaseEntity{
  entity: string;
  key: string;

  /**
   * Which system found this, and the only field that says so: the service resolves the entity name
   * every writer uses down to one canonical name, so `entity` names the class and no longer says
   * who reported it. See `AutomationMessageSource`.
   *
   * Not null on the service and defaulted to AUTOMATION there, so it arrives set - but it is read
   * through `sourceOf` on the panel rather than off the record, which reads an absent value the way
   * the service does and leaves an unrecognised one alone.
   */
  source: AutomationMessageSource;
  value: string;
  message: string;
  read: boolean;
  type: AutomationMessageType;

  /**
   * When the service first stored this message, and when what it says last changed. Both are
   * ISO-8601 instants with an offset, as the service sends them - typed as strings rather than as
   * `Date` because that is what they are at runtime: the store's ingestor passes the payload
   * through untouched, so nothing ever converts them. Read them with `ddMMyyyyHHmmFormat`, which
   * takes either.
   *
   * Null is normal and has to be rendered. The service writes both on the insert, from one clock
   * reading, so a message that has never been revised has them equal - but the columns were added
   * after the table was in use and were not backfilled, because nothing on record says when the
   * rows already there arrived. Those fill in as each message is next reported.
   *
   * Read only on the service (`@JsonProperty(access = READ_ONLY)`), so sending them back in a PUT
   * - which marking a message read does, the whole record going up - changes nothing.
   */
  dateCreated: string | null;
  dateUpdated: string | null;
}

/**
 * The order the sources are laid out in, fixed here rather than taken from the data: the sheet
 * import's findings come first because they are fixed furthest upstream, and a group order that
 * moved with whatever a table happens to hold would move the section a reader is looking for.
 * Anything the service sends that is not named here sorts after both of them.
 */
export const SOURCE_ORDER: string[] = [AutomationMessageSource.AUTOMATION, AutomationMessageSource.CAS];

/**
 * What each source is called on screen and what it means to somebody who has to act on it - the
 * blurb short enough to sit on a group header, the detail on hover for a first reader.
 *
 * Named for the system a reader deals with rather than echoing the stored value: AUTOMATION on a
 * page titled Automation Messages says nothing, and which of the two reported a problem is exactly
 * the thing that says whose hands fix it.
 */
export const SOURCE_ABOUT: Record<string, { label: string; blurb: string; detail: string }> = {
  [AutomationMessageSource.AUTOMATION]: {
    label: 'Sheet import',
    blurb: 'Found in the sheet, and fixed there.',
    detail: 'The Smartsheet import read the row and could not use what it found - a cell that would '
      + 'not convert, a column whose value is not one of the choices. Fixed by editing the sheet, '
      + 'and the message goes when the next import pass reads the corrected row.'
  },
  [AutomationMessageSource.CAS]: {
    label: 'CAS service',
    blurb: 'Found placing the row here, usually master data that is missing.',
    detail: 'This service read the imported row and could not map it onto the model - a name that '
      + 'matched no master data, an offering id that is already somebody else\'s. Usually fixed by '
      + 'adding what is missing here rather than by touching the sheet.'
  }
};

/**
 * The source a message belongs to.
 *
 * The service stores this not null and defaults it to AUTOMATION, so an absent value can only come
 * from one older than the column, and is read the way the service reads one: that stack was the
 * only thing writing these messages at the time. An unrecognised value is left exactly as it stands
 * - the service can gain a source this build has never heard of, and folding it into one of the two
 * named above would file a finding under a system that did not make it.
 */
export const sourceOf = (msg: AutomationMessage): string => msg.source || AutomationMessageSource.AUTOMATION;

/** Known sources in their laid-out order, unknown ones after them and alphabetically among themselves. */
export const compareSource = (a: string, b: string): number => {
  const rank = (value: string) => {
    const at = SOURCE_ORDER.indexOf(value);
    return at === -1 ? SOURCE_ORDER.length : at;
  };
  return rank(a) - rank(b) || a.localeCompare(b);
};

/** The stored value stands in for a source this build cannot name, rather than nothing standing there. */
export const sourceLabel = (value: string): string => SOURCE_ABOUT[value]?.label ?? value;

/** What a source's tooltip says, including for one this build has never heard of. */
export const sourceDetail = (value: string): string => SOURCE_ABOUT[value]?.detail
  ?? `A source this build does not know about. The service files these messages under ${value}.`;

/**
 * Type ordered by how loud it is rather than alphabetically, which would put INFO between ERROR
 * and WARNING. Ascending therefore reads quietest first, so one click on a Type header brings the
 * errors to the top.
 */
export const TYPE_RANK: Record<string, number> = {
  [AutomationMessageType.INFO]: 0,
  [AutomationMessageType.WARNING]: 1,
  [AutomationMessageType.ERROR]: 2
};

/**
 * The loudest type in a set of messages - what a single icon standing for all of them has to
 * carry, since an ERROR hidden behind a warning triangle is the one that would be missed.
 *
 * A type this build cannot rank counts as quieter than every one it can (rank -1) rather than
 * louder: claiming an unknown value is the most severe thing here would put its icon on a class
 * whose real findings are informational. Undefined for an empty set, there being nothing to show.
 */
export const loudestType = (messages: AutomationMessage[]): AutomationMessageType | undefined =>
  messages.reduce<AutomationMessage | undefined>(
    (loudest, msg) =>
      !loudest || (TYPE_RANK[msg.type] ?? -1) > (TYPE_RANK[loudest.type] ?? -1) ? msg : loudest,
    undefined
  )?.type;

/**
 * Whether a message is about a class rather than about some other kind of record.
 *
 * Matched on the name containing 'Klass' instead of against a list of exact names, because the
 * entity name is the reporting side's and has already changed once: the sheet import filed a class
 * under `KlassDTO` while this service filed the same class under `Klass`, and the service now
 * resolves both to one name. `PrivateKlass` still appears in the flag rules' own examples. A test
 * for the one name this build happens to know would silently show nothing on a class whose
 * findings are filed under any of the others, which is the failure worth avoiding here - and the
 * alternative, taking whatever the key matched, would put an instructor's or a facility's finding
 * on a class that shares its number.
 */
export const isKlassMessage = (msg: AutomationMessage): boolean =>
  (msg.entity ?? '').toLowerCase().includes('klass');
